import { format, formatDistanceToNowStrict } from 'date-fns'
import getConfig from 'next/config'
import { toast } from 'react-toastify'
import KP5CONSTANTS from './constants'
import countriesList from './countriesList.json'

const { publicRuntimeConfig } = getConfig()

/**
 * Create a health check object to be displayed on the UI.
 *
 * @param {String} currentEnv Name of UI FE env.
 * @return {Object} health object.
 */
export const healthCheck = async (currentEnv) => {
  const commitSha = process.env.GIT_COMMIT_SHA
  const tagName = process.env.GIT_TAG_NAME

  const envMapping = {}
  const environments = {}

  envMapping[`${process.env.TENANTS_USERS_SERVICE_INTERNAL_URI}/health`] = {
    name: 'kp5-api-tenants-users'
  }
  envMapping[`${process.env.LICENSE_SERVICE_INTERNAL_URI}/health`] = {
    name: 'kp5-api-license'
  }
  envMapping[`${process.env.AUTH_SERVICE_INTERNAL_URI}/health`] = {
    name: 'kp5-api-auth-perm'
  }
  envMapping[`${process.env.USER_RESOURCES_SERVICE_INTERNAL_URI}/health`] = {
    name: 'kp5-api-user-resources'
  }
  envMapping[`${process.env.ADMIN_SERVICE_INTERNAL_URI}/health`] = {
    name: 'kp5-api-admin'
  }
  envMapping[`${process.env.LEGAL_SERVICE_INTERNAL_URI}/health`] = {
    name: 'kp5-api-legal'
  }

  try {
    const promises = Object.keys(envMapping).map((url) => fetch(url))

    // initialize default values
    for (const property in envMapping) {
      if (!environments[envMapping[property].name]) {
        environments[envMapping[property].name] = {
          statusCode: 'Err',
          data: 'Down'
        }
      }
    }
    // make sure we all promises and child promises resolved and catch errors at all levels
    await Promise.all(promises)
      .then(async (responses) => {
        const service = responses.map(async (response) => {
          const data = await response.json().catch((err) => {
            console.log('json() level error: ', err.message)
          })
          const envName = envMapping[response.url].name
          environments[envName] = { data }
        })
        return await Promise.all(service).catch((err) => {
          console.log('service level error: ', err.message)
        })
      })
      .catch((err) => {
        console.log('fetch level error: ', err.message)
      })
  } catch (err) {
    console.log(err.message)
  }
  return {
    [currentEnv]: {
      statusCode: 200,
      data: 'Up',
      meta: {
        commitSha,
        tagName
      },
      environments
    }
  }
}

export const getAdminEmailTypesList = () => {
  return {
    'registration.blacklistedEmailDomains': 'Registration Blacklist Email Domains'
  }
}

/**
 * Transform date into human readable form.
 *
 * @param {string} isoDate unformatted date (e.g. 2016-12-21T00:15:54.907Z).
 * @return {string} Formatted date (e.g. 05/25/2020).
 */
export const formatDate = (isoDate) => {
  if (!isoDate || isoDate === '--' || isoDate === 'Never expires') {
    return isoDate
  } else {
    const day = isoDate.slice(8, 10)
    const month = isoDate.slice(5, 7)
    const year = isoDate.slice(0, 4)

    return `${month}/${day}/${year}`
  }
}

/**
 * @param {string} dateTime date, date and time, unix time (e.g. 2016-12-21T00:15:54.907Z).
 * @param {string} dateFormat date format (e.g. dd MMM yyyy).
 * @return {string} Formatted date (e.g. 21 Dec 2016).
 */
export const formatDateTime = (dateTime, dateFormat = 'dd MMM yyyy') => {
  const newDate = new Date(dateTime)
  if (!dateTime || newDate.toString() === 'Invalid Date') {
    return '-'
  } else if (dateTime === '--' || dateTime === 'Never expires') {
    return dateTime
  }

  return format(newDate, dateFormat)
}

/**
 * Transform date to UTC
 *
 * @param {string} dateObj from new Date().
 * @param {string} dateFormat format of the date to return (e.g. MM/dd/yyyy, yyyy/MM/dd).
 * @return {string} UTC Date, if dateFormat != null, formatted UTC Date
 */
export const dateToUTC = (dateObj, dateFormat = null) => {
  if (!dateObj || dateObj === '--' || dateObj === 'Never expires') {
    return dateObj
  }

  let formattedDate = new Date(
    dateObj.getUTCFullYear(),
    dateObj.getUTCMonth(),
    dateObj.getUTCDate(),
    dateObj.getUTCHours(),
    dateObj.getUTCMinutes(),
    dateObj.getUTCSeconds()
  )

  if (dateFormat) {
    formattedDate = format(formattedDate, dateFormat)
  }

  return formattedDate
}

/**
 * Transform date from API into human readable form.
 *
 * @param {string} apiDate unformatted date (e.g. 20220106).
 * @param {string} dateFormat format of the date to return (e.g. MM/dd/yyyy, yyyy/MM/dd, dd MMM yyyy).
 * @return {string} Formatted date (e.g. 01/06/2022) depending on the format.
 */
export const formatDateFromAPI = (apiDate, dateFormat = 'dd MMM yyyy') => {
  if (!apiDate) {
    return '-'
  } else if (apiDate === '--' || apiDate === 'Never expires') {
    return apiDate
  }
  const year = apiDate.substring(0, 4)
  const month = apiDate.substring(4, 6) - 1
  const day = apiDate.substring(6)

  return format(new Date(year, month, day), dateFormat)
}

/**
 * Transform unix timestamp into human readable form.
 *
 * @param {string} timestamp unformatted date (e.g. 1627950568133).
 * @return {string} Formatted date (e.g. 05/25/2020)
 */
export const formatUnixDate = (unixTimestamp) => {
  const date_ob = new Date(unixTimestamp)
  const year = date_ob.getFullYear()
  const month = ('0' + (date_ob.getMonth() + 1)).slice(-2)
  const day = ('0' + date_ob.getDate()).slice(-2)

  return `${month}/${day}/${year}`
}

/**
 * Transform date into relative (ago) date format.
 *
 * @param {string} isoDate unformatted date (e.g. 2016-12-21T00:15:54.907Z).
 * @return {string} Formatted date (e.g. 24 days ago).
 */
export const formatTimeAgo = (isoDate) => {
  return formatDistanceToNowStrict(new Date(isoDate), { addSuffix: true })
}

export const formatOptions = (options, field) => {
  return options.map((option, id) => {
    if (field) {
      return {
        label: option[field],
        value: option[field],
        id
      }
    } else {
      return {
        label: option,
        value: option,
        id
      }
    }
  })
}

export const getProductsMap = () => {
  return {
    all: '- Any -',
    express: 'Express',
    premium: 'Premium',
    workspace: 'Workspace',
    wprogram: 'Warranty Program',
    m2m: 'm2m',
    plain_emm: 'Plain EMM',
    with_key: 'With Key',
    kc: 'Knox Configurator',
    km: 'Knox Manage',
    'efota-mdm': 'E-FOTA on MDM',
    'e-fota': 'E-FOTA Advanced',
    ks: 'Knox Suite',
    capture: 'Knox Capture',
    kfm: 'Knox E-FOTA One',
    kme: 'Knox Mobile Enrollment',
    kdai: 'Knox Asset Intelligence',
    kscp: 'Knox Care+'
  }
}

export const getIndustryMap = () => {
  return [
    'Communications',
    'Construction',
    'Education',
    'Finance',
    'Government',
    'Healthcare',
    'Hospitality',
    'Manufacturing',
    'Professional Services',
    'Retail',
    'Transportation & Logistics',
    'Utility',
    'Others'
  ]
}

export const getCompanySizeMap = () => {
  return [
    {
      name: '1-19',
      value: 'Tiny'
    },
    {
      name: '20-49',
      value: 'Small'
    },
    {
      name: '50-99',
      value: 'Medium'
    },
    {
      name: '100-499',
      value: 'Large'
    },
    {
      name: '500-999',
      value: 'Huge'
    },
    {
      name: '1000-2499',
      value: 'Enterprise'
    },
    {
      name: '2500-4999',
      value: 'Enterprise2'
    },
    {
      name: '5000+',
      value: 'Enterprise3'
    }
  ]
}

/**
 * Check if all required fields are filled out.
 *
 * @param {object} formSchema Formik form schema
 * @param {object} values Formik form values
 * @return {boolean} True/False value if all required fields are filled out
 */
export const isAllRequiredFieldsValid = (formSchema, values) => {
  return Object.keys(formSchema.fields)
    .filter((field) => !!formSchema.fields[field].exclusiveTests.required)
    .some((key) => (!Array.isArray(values[key]) ? !values[key] : !values[key].length))
}

/**
 * Create random string.
 * Not cryptographically secure, nor is randomness guaranteed. Use at own risk.
 *
 * Examples:
 *   randomString(16, 'aA');  --> will use only alpha (lowercase and uppercase)
 *   randomString(32, '#aA'); --> will use alpha and numbers.
 *   randomString(64, '#A!'); --> will use alpha, numbers and special characters
 *
 * @param length
 * @param chars
 * @returns {string}
 */
export const randomString = (length, chars) => {
  let mask = ''
  if (chars.indexOf('a') > -1) mask += 'abcdefghijklmnopqrstuvwxyz'
  if (chars.indexOf('A') > -1) mask += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  if (chars.indexOf('#') > -1) mask += '0123456789'
  if (chars.indexOf('!') > -1) mask += '~`!@#$%^&*()_+-={}[]:";\'<>?,./|\\'
  let result = ''
  for (let i = length; i > 0; --i) result += mask[Math.floor(Math.random() * mask.length)]
  return result
}

export const convertFiltersToUrlParams = (apiFilters) => {
  const searchParams = new URLSearchParams()
  Object.keys(apiFilters).forEach((key) => {
    // Arrays need to follow search param format.
    if (Array.isArray(apiFilters[key])) {
      const arrayFields = apiFilters[key]
      arrayFields.forEach((field) => {
        if (typeof field == 'object') {
          searchParams.append(key, JSON.stringify(field))
        } else if (field !== '') {
          searchParams.append(key, field.toString())
        }
      })
    } else {
      searchParams.append(key, apiFilters[key])
    }
  })
  return searchParams
}

/**
 * Check if an email is considered a subsidiary based on the domain.
 *
 * @param {string} email
 * @return {boolean} True/False
 */
export const isSubsidiaryEmail = (email) => {
  if (typeof email !== 'string') {
    return false
  }
  const subDomains = ['samsung.com', 'partner.samsung.com']
  const userDomain = email.split('@')[1]
  return subDomains.includes(userDomain)
}

/**
 * Utility to get top level base domain from a url.
 *
 * @param baseUrl
 * @returns {*}
 */
export const getBaseDomain = (baseUrl) => {
  // Remove protocol first
  baseUrl = baseUrl.split('//').pop()
  return baseUrl.split('.').slice(-2).join('.')
}

/**
 * Return http response setHeader array of set-cookie headers for user login. We use httpOnly
 * for kpUserToken_jwt cookie is not visible to the client.
 *
 * IMPORTANT: Due to the security implications of httpOnly, secure, etc please
 * consult with other developers before making changes.
 *
 * Set-Cookie strings that were moved here from API. We can omit "Domain" so that current
 * domain is used:
 *   kpUserToken_jwt=<jwt token>; Max-Age=3600; Expires=Thu, 23 Dec 2021 12:03:26 GMT; Secure; HttpOnly; SameSite=Lax; Domain=.kp5-local.samsungknox.com; Path=/
 *
 * @param {string} token
 * @returns {string[]}
 */
export const getLoginSetCookieHeader = (token) => {
  // 1 hour matches the "expire" time of kp5-api-auth-perm kpSessions records.
  const ttl = 3600
  const expires = new Date(new Date().getTime() + ttl * 1000).toUTCString()
  return [
    `kpUserToken_jwt=${encodeURIComponent(
      token
    )}; Max-Age=${ttl}; Expires=${expires}; Secure; HttpOnly; SameSite=Lax; Path=/`
  ]
}

/**
 * Similar to getLoginSetCookieHeader() but for admin UI.
 *
 * @param {string} token
 * @returns {string[]}
 */
export const getLoginSetCookieHeaderAdmin = (token) => {
  const loggedInCookieName = 'kpLoggedIn_admin'
  // 1 hour matches the "expire" time of kp5-api-auth-perm kpSessions records.
  const ttl = 3600
  const expires = new Date(new Date().getTime() + ttl * 1000).toUTCString()
  return [
    `kpUserToken_jwt=${encodeURIComponent(
      token
    )}; Max-Age=${ttl}; Expires=${expires}; Secure; HttpOnly; SameSite=Lax; Path=/`,
    `${loggedInCookieName}=1; Max-Age=${ttl}; Expires=${expires}; Secure; SameSite=Lax; Path=/`
  ]
}

/**
 * Return http response setHeader array of set-cookie headers for user logout.
 *
 * Set-Cookie strings that were moved here from API. We can omit "Domain" so that current
 * domain is used:
 *   kpUserToken_jwt=; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure; HttpOnly; SameSite=Lax; Domain=.kp5-local.samsungknox.com; Path=/
 *
 * @returns {string[]}
 */
export const getLogoutSetCookieHeader = () => {
  return [
    `kpUserToken_jwt=; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure; HttpOnly; SameSite=Lax; Path=/`
  ]
}

/**
 * Similar to getLogoutSetCookieHeader() but for admin UI.
 *
 * @returns {string[]}
 */
export const getLogoutSetCookieHeaderAdmin = () => {
  const loggedInCookieName = 'kpLoggedIn_admin'
  return [
    `kpUserToken_jwt=; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure; HttpOnly; SameSite=Lax; Path=/`,
    `${loggedInCookieName}=; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure; SameSite=Lax; Path=/`
  ]
}

/**
 * Add returnUrl Set-Cookie string.
 *
 * @param value
 * @returns {string}
 */
export const getReturnUrlSetCookieAddString = (value) => {
  const ttl = 3600
  const expires = new Date(new Date().getTime() + ttl * 1000).toUTCString()
  return `returnUrl=${encodeURIComponent(
    value
  )}; Max-Age=${ttl}; Expires=${expires}; Secure; Path=/`
}

/**
 * Delete returnUrl Set-Cookie string.
 *
 * @returns {string}
 */
export const getReturnUrlSetCookieDeleteString = () => {
  return `returnUrl=; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:01 GMT, Secure; Path=/`
}

/**
 * Delete USP unified_redirection Set-Cookie string.
 *
 * @returns {string}
 */
export const getUnifiedRedirectSetCookieDeleteString = () => {
  const baseDomain = getBaseDomain(publicRuntimeConfig.publicAppBaseUrl)
  return `unified_redirection=; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:01 GMT, Secure; SameSite=Lax; Domain=${baseDomain}; Path=/`
}

export const getAmsAuthCookieNamePrefix = () => {
  // NOTE: Keyed by the KP5 UI env var "DEPLOYMENT_ENV". Values are AMS authToken cookie env prefixes.
  const amsDeploymentEnvMapping = {
    local: publicRuntimeConfig.publicLocalAmsDev ?? 'stg',
    usqa: 'qa',
    euqa: 'qa',
    usstg: 'stg',
    eustg: 'stg',
    euprd: 'prd',
    euuat: 'uat'
  }
  return amsDeploymentEnvMapping[publicRuntimeConfig.deploymentEnv] ?? ''
}

export const getAmsAuthTokenCookieName = () => {
  return `${getAmsAuthCookieNamePrefix()}-authToken`
}

export const getAmsAuthLoggedInCookieName = () => {
  return `${getAmsAuthCookieNamePrefix()}-loggedIn`
}

/**
 * Delete AMS authToken cookie
 *
 * @returns {string}
 */
export const getUAAuthtokenSetCookieDeleteString = () => {
  const baseDomain = getBaseDomain(publicRuntimeConfig.publicAppBaseUrl)
  return `${getAmsAuthTokenCookieName()}=; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:01 GMT, Secure; SameSite=Lax; Domain=${baseDomain}; Path=/`
}

/**
 * Delete AMS loggedIn cookie
 *
 * @returns {string}
 */
export const getUALoggedInSetCookieDeleteString = () => {
  const baseDomain = getBaseDomain(publicRuntimeConfig.publicAppBaseUrl)
  return `${getAmsAuthLoggedInCookieName()}=; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:01 GMT, Secure; SameSite=Lax; Domain=${baseDomain}; Path=/`
}

/**
 * Helper to parse an http header cookie string into key/values.
 *
 * @param cookieString
 * @returns {[string,string][]}
 */
export const getEntriesFromCookie = (cookieString = '') => {
  return cookieString.split(';').map((pair) => {
    const indexOfEquals = pair.indexOf('=')
    let name
    let value
    if (indexOfEquals === -1) {
      name = ''
      value = pair.trim()
    } else {
      name = pair.substr(0, indexOfEquals).trim()
      value = pair.substr(indexOfEquals + 1).trim()
    }
    const firstQuote = value.indexOf('"')
    const lastQuote = value.lastIndexOf('"')
    if (firstQuote !== -1 && lastQuote !== -1) {
      value = value.substring(firstQuote + 1, lastQuote)
    }
    return [name, value]
  })
}

export const knoxLoginUrls = () => {
  return {
    caseStudies: '/case-studies',
    aboutKnox: {
      newsUrl: '/blog',
      aboutKnoxUrl: '/about-knox',
      knoxFeaturesAndDevicesUrl: '/about-knox#features-and-devices',
      knoxPlatformUrl: '/knox-platform',
      knoxSecurityUrl: '/knox-platform/knox-security',
      knoxCertificationsUrl: '/knox-platform/knox-certifications',
      supportedDevicesUrl: '/knox-platform/supported-devices',
      knoxOnAndroidUrl: '/knox-platform/knox-on-android',
      knoxOnTizenUrl: '/knox-platform/knox-on-tizen-wearables',
      caseStudies: '/case-studies',
      securedByKnox: '/secured-by-knox'
    },
    solutions: {
      buyFromAResellerUrl: '/resellers',
      forResellersUrl: '/for-resellers',
      knoxGuardUrl: '/solutions/it-solutions/knox-guard',
      itSolutionsUrl: '/solutions/it-solutions',
      deviceCustomizationUrl: '/solutions/device-customizaton',
      knoxSuite: '/solutions/it-solutions#knox-suite',
      knoxConfigureUrl: '/solutions/it-solutions/knox-configure',
      knoxCaptureUrl: '/solutions/it-solutions/knox-capture',
      knoxMobileEnrollmentUrl: '/solutions/it-solutions/knox-mobile-enrollment',
      knoxPremiumUrl: '/solutions/it-solutions/knox-premium',
      knoxManageUrl: '/solutions/it-solutions/knox-manage',
      knoxWorkspaceUrl: '/solutions/it-solutions/knox-platform-for-enterprise',
      samsungEFOTAUrl: '/solutions/it-solutions/samsung_e-fota',
      knoxDAIURL: '/solutions/it-solutions/knox-intelligence',
      samsungCarePlusUrl: '/samsung-care-plus',
      knoxOnAdroidUrl: '',
      knoxOnTizenUrl: '',
      samsungGalaxyEE: '/enterprise-edition',
      samsungETS: '/enterprise-tech-support',
      partnerSolutionsUrl: '/solutions/partner-solutions',
      developerToolsUrl: '/solutions/developer-tools',
      knoxSDKUrl: '/solutions/developer-tools/knox-sdks',
      personalAppsUrl: '/solutions/personal-apps',
      secureFolderUrl: '/solutions/personal-apps/secure-folder',
      newLook: '/blog/samsung-knox-to-release-a-major-update-with-redesigned-kme-kc-kdp-consoles'
    },
    partners: {
      forDevelopers: publicRuntimeConfig.developerBaseUrl,
      forPartners: publicRuntimeConfig.partnerBaseUrl,
      forMSPs: `${publicRuntimeConfig.partnerBaseUrl}/msp`,
      forResellers: `${publicRuntimeConfig.partnerBaseUrl}/resellers`
    },
    support: '/support',
    purchaseUrl: '/resellers',
    newsUrl: '/blog',
    tryFreeUrl: '/free-trial-guide#all_solutions',
    kp4BaseUrl: publicRuntimeConfig.kp4BaseUrl
  }
}

/**
 * Get a product title for a given productParam.
 *
 * Note that productParam is "null" for default /register flow
 *
 * @param param
 * @returns {*}
 */
export const knoxRegistrationSolutionName = (param) => {
  const map = {
    [KP5CONSTANTS.REGISTER_PRODUCTS.EFOTA_ONE]: 'Knox E-FOTA',
    [KP5CONSTANTS.REGISTER_PRODUCTS.MANAGE]: 'Knox Manage',
    [KP5CONSTANTS.REGISTER_PRODUCTS.GUARD]: 'Knox Guard',
    [KP5CONSTANTS.REGISTER_PRODUCTS.CONFIGURATOR]: 'Knox Configure',
    [KP5CONSTANTS.REGISTER_PRODUCTS.DEPLOYMENT_PROGRAM]: 'Knox Reseller Portal',
    [KP5CONSTANTS.REGISTER_PRODUCTS.MOBILE_ENROLLMENT]: 'Knox Mobile Enrollment',
    [KP5CONSTANTS.REGISTER_PRODUCTS.PLATFORM_FOR_ENTERPRISE]: 'Knox Platform for Enterprise',
    [KP5CONSTANTS.REGISTER_PRODUCTS.MANAGED_SERVICE_PROVIDER]: 'Knox MSP Portal',
    [KP5CONSTANTS.REGISTER_PRODUCTS.EE_DEVICES]: 'Enterprise Edition dashboard',
    [KP5CONSTANTS.REGISTER_PRODUCTS.CLOUD_SERVICES]: 'Knox cloud services',
    [KP5CONSTANTS.REGISTER_PRODUCTS.CAPTURE]: 'Knox Capture',
    [KP5CONSTANTS.REGISTER_PRODUCTS.CARE_PLUS]: 'Samsung Care+ for Business',
    [KP5CONSTANTS.REGISTER_PRODUCTS.DEVICE_ASSET_INTELLIGENCE]: 'Knox Asset Intelligence',
    [KP5CONSTANTS.REGISTER_PRODUCTS.DEVICE_ASSET_INTELLIGENCE_ALIAS]: 'Knox Asset Intelligence'
  }
  // IMPORTANT: productParam will be null in the /register case. CLOUD_SERVICES
  // is considered the default.
  return map[param] ?? map[KP5CONSTANTS.REGISTER_PRODUCTS.CLOUD_SERVICES]
}

/**
 * Get a product tile ID for a given productParam.
 *
 * Note that productParam is "null" for default /register flow
 *
 * @param param
 * @returns {*}
 */
export const knoxRegistrationProductsWithPathsTileID = (param) => {
  const map = {
    [KP5CONSTANTS.REGISTER_PRODUCTS.DEFAULT]: 'knoxMobileEnrollment', // Note: There's auto approval across all KCS services except SCB and KG. Hence, we will use KME's status to represent status of KCS services.
    [KP5CONSTANTS.REGISTER_PRODUCTS.CAPTURE]: 'knoxCapture',
    [KP5CONSTANTS.REGISTER_PRODUCTS.PLATFORM_FOR_ENTERPRISE]: 'knoxPlatformForEnterprise',
    [KP5CONSTANTS.REGISTER_PRODUCTS.DEPLOYMENT_PROGRAM]: 'knoxDeploymentProgram',
    [KP5CONSTANTS.REGISTER_PRODUCTS.GUARD]: 'knoxGuard',
    [KP5CONSTANTS.REGISTER_PRODUCTS.CARE_PLUS]: 'samsungCareForBusiness',
    [KP5CONSTANTS.REGISTER_PRODUCTS.MANAGED_SERVICE_PROVIDER]: 'managedServiceProvider'
  }
  return map[param] ?? map[KP5CONSTANTS.REGISTER_PRODUCTS.DEFAULT]
}

export const scpePrivacyPolicyLegalTypes = () => {
  return [
    'privacy_policy_ko_scpe',
    'privacy_policy_in',
    'privacy_policy_ph',
    'privacy_policy_us_scpe',
    'privacy_policy_es_scpe',
    'privacy_policy_sena_scpe',
    'privacy_policy_fr_scpe',
    'privacy_policy_de_scpe',
    'privacy_policy_id_scpe',
    'privacy_policy_sg_scpe',
    'privacy_policy_pl_scpe',
    'privacy_policy_ro_scpe',
    'privacy_policy_ae_scpe',
    'privacy_policy_vn_scpe',
    'privacy_policy_bx_scpe',
    'privacy_policy_cz_scpe',
    'privacy_policy_my_scpe',
    'privacy_policy_ch_scpe',
    'privacy_policy_mx_scpe',
    'privacy_policy_sk_scpe',
    'privacy_policy_tw_scpe',
    'privacy_policy_it_scpe',
    'privacy_policy_ca_scpe',
    'privacy_policy_at_scpe',
    'privacy_policy_au_scpe',
    'privacy_policy_br_scpe',
    'privacy_policy_hk_scpe',
    'privacy_policy_mo_scpe',
    'privacy_policy_tr_scpe',
    'privacy_policy_is_scpe',
    'privacy_policy_sa_scpe',
    'privacy_policy_gb_scpe'
  ]
}

export const getSupportedPrivacyPolicyLocaleStaticMap = () => {
  // map allows flexibility for when we want to reuse same component for different locale
  return {
    ar: 'ar',
    bn_bd: 'bn-bd',
    'eu-bg': 'eu-bg',
    'eu-bs_ba': 'eu-bs-ba',
    'eu-ca': 'eu-ca',
    'eu-cs': 'eu-cs',
    'eu-da': 'eu-da',
    'eu-de': 'eu-de',
    'eu-et': 'eu-et',
    'eu-el_gr': 'eu-el-gr',
    'eu-es_es': 'eu-es-es',
    'eu-fi': 'eu-fi',
    'eu-fr': 'eu-fr',
    'eu-hr': 'eu-hr',
    'eu-hu': 'eu-hu',
    'eu-is': 'eu-is',
    'eu-it_it': 'eu-it-it',
    'eu-lt': 'eu-lt',
    'eu-lv': 'eu-lv',
    'eu-mk': 'eu-mk',
    'eu-nb': 'eu-nb',
    'eu-nl': 'eu-nl',
    'eu-pl': 'eu-pl',
    'eu-pt_pt': 'eu-pt-pt',
    'eu-ro': 'eu-ro',
    'eu-sk': 'eu-sk',
    'eu-sl': 'eu-sl',
    'eu-sq': 'eu-sq',
    'eu-sr': 'eu-sr',
    'eu-sv': 'eu-sv',
    es_us: 'es-us',
    fa: 'fa',
    fr: 'fr',
    hi: 'hi',
    hy: 'hy',
    id: 'id',
    ja_jp: 'ja-jp',
    ka: 'ka',
    kk: 'kk',
    km: 'km',
    ky_kg: 'ky-kg',
    lo: 'lo',
    mn_mn: 'mn-mn',
    ms: 'ms',
    my_zg: 'my-zg',
    ne: 'NE',
    nl: 'NL',
    pt_pt: 'pt-pt',
    ru_ru: 'ru-ru',
    si: 'si',
    th: 'th',
    tg_tj: 'tg-tj',
    tk_tm: 'tk-tm',
    tl: 'tl',
    uk: 'uk',
    uz: 'uz',
    zh_cn: 'zh-cn',
    zh_hk: 'zh-hk',
    zh_tw: 'zh-tw'
  }
}

export const getSupportedPrivacyPolicyLocaleDynamicMap = () => {
  //supported locale and corresponding legalType for the locale
  return {
    ko: 'privacy_policy_ko',
    'ko-kaa': 'privacy_policy_ko_kaa',
    br: 'privacy_policy_br',
    'br-kaa': 'privacy_policy_br_kaa',
    tk: 'privacy_policy_tk',
    'tk-kaa': 'privacy_policy_tk_kaa',
    us: 'privacy_policy_usa',
    'us-kaa': 'privacy_policy_usa_kaa',
    eu: 'privacy_policy_eu',
    'eu-kaa': 'privacy_policy_eu_kaa',
    latam: 'privacy_policy_latam',
    'latam-kaa': 'privacy_policy_latam_kaa',
    cn: 'privacy_policy_cn',
    vn: 'privacy_policy_vn',
    'vn-kaa': 'privacy_policy_vn_kaa'
  }
}

export const getKpUserAmsRegion = (loggedInUser) => {
  let region = 'US'
  if (loggedInUser?.amsRegions && loggedInUser.amsRegions[0]) {
    region = loggedInUser.amsRegions[0]
  }
  return region
}

/**
 * Return products whose registration UI is solely on the dashboard. These are products
 * without legal agreements. User clicks the tile and are immediately shown a success/fail
 * message in place.
 *
 * @returns {[string,string]}
 */
export const getProductsWithOneStepRegistration = () => {
  // Background:
  // For authed users, products that do *not* have legal agreement step do not have
  // a request-access route. This is a UX choice. There are 2 ways an existing user gets
  // to a product register page:
  // 1) Pre-login, if they use /register/* path, after login we redirect immediately
  // to product registration.
  // 2) User is on dashboard and clicks tile to enroll.

  // We don't want to support multiple registration paths, so we have chosen 1 way
  // to enroll these "1 step" enrollments. Using a request-access redirect for these
  // would work if we could just use a gSSP "redirect" from the request-access page.
  // But we cannot, because we must be able to pass (potentially complex) error info
  // back to dashboard. This requires client side routing via useEffect. This results
  // in a jumpy and very-not-seamless experience.
  return [
    KP5CONSTANTS.REGISTER_PRODUCTS.CAPTURE,
    KP5CONSTANTS.REGISTER_PRODUCTS.PLATFORM_FOR_ENTERPRISE
  ]
}

/**
 * Copy to clipboard the content of target
 * @param {string} target (e.g. e.currentTarget.parentNode.previousSibling)
 */
export const copyText = (target, value) => {
  // Don't proceed if no target or target value is present
  if (target && !target.value) return
  else if (!target && value) {
    navigator.clipboard.writeText(value)
    toast.success('Copied!')
    return
  }

  // Select the text field
  target.select()
  target.setSelectionRange(0, 99999) // For mobile devices

  // Copy the text inside the text field
  navigator.clipboard.writeText(target.value)

  // Notify the user that the selected text has been copied successfuly
  toast.success('Copied!')
}

export const isValidKpRedirectUrl = (redirectUrl) => {
  if (!redirectUrl) {
    return false
  } else if (redirectUrl.startsWith('/')) {
    // relative path
    // using dummy origin to verify the origin stays unchanged after redirect (refer to KP5-4599)
    try {
      const parsedUrl = new URL(decodeURIComponent(redirectUrl), 'https://dummy.com')
      return parsedUrl.origin === 'https://dummy.com'
    } catch (error) {
      return false
    }
  } else if (
    // absolute path
    redirectUrl.startsWith(publicRuntimeConfig.kp4BaseUrl) ||
    redirectUrl.startsWith(publicRuntimeConfig.publicAppBaseUrl) ||
    redirectUrl.startsWith(publicRuntimeConfig.adminAppBaseUrl) ||
    redirectUrl.startsWith(publicRuntimeConfig.uspUABaseUrl)
  ) {
    return true
  }

  return false
}

export const knoxDashboardTileOverlay = (status, params = {}, overrideOverlay = null) => {
  if (overrideOverlay) return overrideOverlay

  let title = null
  let content = null

  switch (status) {
    case KP5CONSTANTS.KCS_STATUSES.JOIN:
      title = 'Access Granted'
      content = (
        <p>
          <a rel="noreferrer" href="#" onClick={params.joinAction}>
            Click here
          </a>{' '}
          to get access to the admin console for {params.solutionName}. Your super admin will need
          to assign you the admin role.{' '}
          <a
            rel="noreferrer"
            target="_blank"
            href="https://docs.samsungknox.com/admin/fundamentals/faqs/index.htm"
          >
            Learn more
          </a>{' '}
          about account management.
        </p>
      )
      break

    case KP5CONSTANTS.KCS_STATUSES.INVITED_ADMIN:
      title = 'Request Access'
      content = (
        <p>
          In order to use {params.solutionName}, your super admin must first register for the
          solution.{' '}
          <a rel="noreferrer" href="#" onClick={params.joinAction}>
            Click here
          </a>{' '}
          to send a request email to the super admin or{' '}
          <a
            rel="noreferrer"
            target="_blank"
            href="https://docs.samsungknox.com/admin/fundamentals/faqs/index.htm"
          >
            learn more
          </a>{' '}
          about account management.
        </p>
      )
      break

    case KP5CONSTANTS.KCS_STATUSES.REQUESTED_ADMIN:
      title = 'Access Requested'
      content = (
        <p>
          In order to use {params.solutionName}, your super admin must first register for the
          solution. Your request to the super admin has been sent.{' '}
          <a
            rel="noreferrer"
            target="_blank"
            href="https://docs.samsungknox.com/admin/fundamentals/faqs/index.htm"
          >
            Learn more
          </a>{' '}
          about account management.
        </p>
      )
      break

    case KP5CONSTANTS.KCS_STATUSES.ACTIVE:
      content = (
        <a
          className="launch-console-link btn btn-outline-primary btn-inverse"
          href={params.consoleUrl}
        >
          LAUNCH CONSOLE
        </a>
      )
      break
  }

  return (
    <div className="pl-2 pr-2 subadmin-overlay">
      {title && <h4>{title}</h4>}
      {content}
    </div>
  )
}

export const knoxDashboardKcsLabelByStatus = (status) => {
  const labels = {
    [KP5CONSTANTS.KCS_STATUSES.PENDING]: 'Pending',
    [KP5CONSTANTS.KCS_STATUSES.REJECTED]: 'Rejected',
    [KP5CONSTANTS.KCS_STATUSES.BLOCKED]: 'Blocked',
    [KP5CONSTANTS.KCS_STATUSES.INVITED_ADMIN]: 'Contact Admin',
    [KP5CONSTANTS.KCS_STATUSES.REQUESTED_ADMIN]: 'Requested',
    [KP5CONSTANTS.KCS_STATUSES.UNAVAILABLE]: 'Not Available',
    [KP5CONSTANTS.KCS_STATUSES.AF_APPROVED]: 'Not Available',
    [KP5CONSTANTS.KCS_STATUSES.JOIN]: 'Join',
    [KP5CONSTANTS.KCS_STATUSES.KNOX_SUITE]: 'Knox Suite'
  }

  return labels[status]
}

/**
 * Return KPP+ redirect URL if the conditions are met
 * @param {Array} services
 * @returns {string}
 */
export const getKppRedirectforMspKdp = (services = []) => {
  let redirectUrl = null
  if (services?.includes('KPP') && ['RP', 'MSP'].some((product) => services?.includes(product))) {
    redirectUrl = `${publicRuntimeConfig.partnerBaseUrl}/dashboard`
  }
  return redirectUrl
}

export const downloadObjectAsJson = (obj, fileName) => {
  const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(obj, null, 4))
  const downloadAnchorNode = document.createElement('a')
  downloadAnchorNode.setAttribute('href', dataStr)
  downloadAnchorNode.setAttribute('download', fileName)
  document.body.appendChild(downloadAnchorNode)
  downloadAnchorNode.click()
  downloadAnchorNode.remove()
}

export const knoxEfotaFirmwareRequestStatus = (status) => {
  const labels = {
    P: 'Pending', // Processing
    M: 'Pending', // Migration
    U: 'Pending', // Upload
    R: 'Complete', // Ready
    C: 'Complete', // Complete
    N: 'Error', // Fail - Notified
    F: 'Error' // Fail
  }

  return labels[status]
}

export const getCountryDetails = (iso) => {
  return countriesList.filter((country) => country.iso2.toUpperCase() === iso.toUpperCase()).pop()
}

/**
 * Return convertedValue of given bytes
 * @param {String} bytes
 * @param {Number} decimals
 * @returns {string} convertedValue
 */
export const convertBytes = (bytes, decimals = 2) => {
  if (!+bytes) return '0 Byte'
  let convertedValue = null

  const dm = decimals < 0 ? 0 : decimals
  const k = 1024 // in binary
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  convertedValue = `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
  return convertedValue
}

/**
 * This func helps return the correct translation key all the extra form labels defined in
 * kp5-api-user-resources/node_modules/@srca-b2b/kp5-common/lib/addressConfig.js
 * Variations of the label:
 * 1. Address line 1 => Address_line_1
 * 2. Postal code => Postal_code
 * 3. City/Regency => City_Regency
 * 4. City / Oblast => City_Oblast
 * @param {*} label
 * @returns correct key version of the label.
 */
export const getLocaleFormLabelKey = (formLabel) => {
  if (formLabel) {
    let localizedLabel = formLabel
    if (formLabel.indexOf('/') >= 0) {
      localizedLabel = formLabel.replace(/\s/g, '').replace(/\//g, '_')
    } else {
      localizedLabel = formLabel.replace(/\s/g, '_')
    }
    return localizedLabel
  } else {
    return ''
  }
}

/**
 * This func helps to resolve the issues with portguese locale
 * @param {*} locale
 * @returns if locale has portguese return the correct locale else return the original locale
 */
export const correctPTlocale = (locale) => {
  if (locale.toLowerCase() === 'pt-br') {
    return 'pt-BR'
  } else {
    return locale
  }
}
